import React from "react";

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
      padding: "1rem",
      overflowY: "auto",
    },
    modal: {
      backgroundColor: "white",
      borderRadius: "12px",
      maxWidth: "900px",
      width: "100%",
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },
    header: {
      padding: "1.5rem 2rem",
      borderBottom: "2px solid #e5e7eb",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "sticky",
      top: 0,
      backgroundColor: "white",
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
    },
    title: {
      fontSize: "1.875rem",
      fontWeight: "700",
      color: "#1f2937",
      margin: 0,
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "1.5rem",
      cursor: "pointer",
      color: "#6b7280",
      padding: "0.5rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "6px",
      transition: "all 0.2s",
    },
    content: {
      padding: "2rem",
      overflowY: "auto",
      fontFamily: "system-ui, -apple-system, sans-serif",
      lineHeight: "1.6",
      color: "#333",
    },
    subtitle: {
      fontSize: "0.95rem",
      color: "#6b7280",
      fontWeight: "500",
      marginBottom: "2rem",
    },
    section: {
      marginBottom: "2rem",
    },
    sectionTitle: {
      fontSize: "1.25rem",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "0.75rem",
      paddingBottom: "0.5rem",
      borderBottom: "1px solid #e5e7eb",
    },
    subsectionTitle: {
      fontSize: "1.1rem",
      fontWeight: "600",
      color: "#4b5563",
      marginBottom: "0.5rem",
      marginTop: "1rem",
    },
    paragraph: {
      marginBottom: "0.75rem",
      color: "#374151",
      fontSize: "0.95rem",
    },
    list: {
      paddingLeft: "1.5rem",
      marginBottom: "0.75rem",
    },
    listItem: {
      marginBottom: "0.4rem",
      color: "#374151",
      fontSize: "0.95rem",
    },
    strong: {
      fontWeight: "600",
      color: "#1f2937",
    },
    contact: {
      backgroundColor: "#f9fafb",
      padding: "1.25rem",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
      marginTop: "1rem",
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h1 style={styles.title}>Privacy Policy</h1>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#f3f4f6";
              e.target.style.color = "#1f2937";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "#6b7280";
            }}
          >
            ✕
          </button>
        </div>

        <div style={styles.content}>
          <p style={styles.subtitle}>
            <strong>Effective Date:</strong> January 1, 2024 |{" "}
            <strong>Last Updated:</strong> January 1, 2024
          </p>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>1. Introduction</h2>
            <p style={styles.paragraph}>
              Welcome to Arkila's Privacy Policy. This document explains how
              Arkila ("we," "our," or "us") collects, uses, processes, and
              protects information through our transportation booking platform
              and administrative systems.
            </p>
            <p style={styles.paragraph}>This Privacy Policy applies to:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Arkila mobile and web applications
              </li>
              <li style={styles.listItem}>Arkila administrative dashboard</li>
              <li style={styles.listItem}>
                All related services and platforms
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>2. Information We Collect</h2>

            <h3 style={styles.subsectionTitle}>2.1 Personal Information</h3>
            <p style={styles.paragraph}>
              We collect the following types of personal information:
            </p>

            <p style={styles.paragraph}>
              <span style={styles.strong}>Account Information:</span>
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Full name and contact details</li>
              <li style={styles.listItem}>Email address and phone number</li>
              <li style={styles.listItem}>
                Profile pictures and identification documents
              </li>
              <li style={styles.listItem}>
                Payment information and billing addresses
              </li>
            </ul>

            <p style={styles.paragraph}>
              <span style={styles.strong}>Booking and Trip Information:</span>
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Origin and destination locations</li>
              <li style={styles.listItem}>
                Trip dates, times, and preferences
              </li>
              <li style={styles.listItem}>
                Passenger counts and special requirements
              </li>
              <li style={styles.listItem}>Booking history and patterns</li>
            </ul>

            <h3 style={styles.subsectionTitle}>2.2 Location Information</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Real-time GPS location during trips
              </li>
              <li style={styles.listItem}>Pickup and drop-off locations</li>
              <li style={styles.listItem}>Route information and waypoints</li>
              <li style={styles.listItem}>
                Location history for service improvement
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>3. How We Use Your Information</h2>

            <h3 style={styles.subsectionTitle}>3.1 Core Services</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Processing and managing bookings</li>
              <li style={styles.listItem}>
                Facilitating transportation services
              </li>
              <li style={styles.listItem}>Real-time tracking and navigation</li>
              <li style={styles.listItem}>Payment processing and billing</li>
            </ul>

            <h3 style={styles.subsectionTitle}>3.2 Safety and Security</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Verifying user identities</li>
              <li style={styles.listItem}>
                Monitoring for fraudulent activity
              </li>
              <li style={styles.listItem}>
                Ensuring passenger and operator safety
              </li>
              <li style={styles.listItem}>Maintaining platform security</li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              4. Information Sharing and Disclosure
            </h2>

            <h3 style={styles.subsectionTitle}>4.1 Service Providers</h3>
            <p style={styles.paragraph}>
              We share information with trusted third-party service providers
              who assist us in:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Payment processing</li>
              <li style={styles.listItem}>Cloud storage and computing</li>
              <li style={styles.listItem}>
                Analytics and performance monitoring
              </li>
              <li style={styles.listItem}>Customer support services</li>
            </ul>

            <h3 style={styles.subsectionTitle}>4.2 Transportation Operators</h3>
            <p style={styles.paragraph}>
              We share necessary information with jeepney operators including:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Passenger contact information for coordination
              </li>
              <li style={styles.listItem}>Pickup and destination details</li>
              <li style={styles.listItem}>Trip schedules and requirements</li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>5. Data Security</h2>
            <p style={styles.paragraph}>
              We implement comprehensive security measures including:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                <span style={styles.strong}>Encryption:</span> All sensitive
                data is encrypted in transit and at rest
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Access Controls:</span> Strict
                access controls for administrative systems
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Regular Audits:</span> Security
                assessments and vulnerability testing
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Incident Response:</span> Procedures
                for handling security incidents
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>6. Your Rights and Choices</h2>
            <p style={styles.paragraph}>
              You have the following rights regarding your personal information:
            </p>

            <h3 style={styles.subsectionTitle}>6.1 Access and Portability</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Request copies of your personal information
              </li>
              <li style={styles.listItem}>
                Export your data in a portable format
              </li>
            </ul>

            <h3 style={styles.subsectionTitle}>6.2 Correction and Updates</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Update or correct inaccurate information
              </li>
              <li style={styles.listItem}>
                Modify account preferences and settings
              </li>
            </ul>

            <h3 style={styles.subsectionTitle}>6.3 Deletion</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Request deletion of your personal information
              </li>
              <li style={styles.listItem}>Close your account permanently</li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>7. Data Retention</h2>
            <p style={styles.paragraph}>
              We retain personal information for the following periods:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                <span style={styles.strong}>Active Users:</span> For the
                duration of account activity
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Booking Records:</span> 7 years for
                legal and tax purposes
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Payment Information:</span> As
                required by financial regulations
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Location Data:</span> 1 year unless
                required for safety investigations
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>8. Contact Information</h2>
            <div style={styles.contact}>
              <p style={styles.paragraph}>
                <span style={styles.strong}>Privacy Team</span>
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Email: privacy@arkila.com</li>
                <li style={styles.listItem}>Phone: +63 (2) 8XXX-XXXX</li>
                <li style={styles.listItem}>Response Time: 30 days maximum</li>
              </ul>

              <p style={styles.paragraph}>
                <span style={styles.strong}>Data Protection Officer</span>
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Email: dpo@arkila.com</li>
                <li style={styles.listItem}>Phone: +63 (2) 8XXX-XXXX</li>
              </ul>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>9. Updates to This Policy</h2>
            <p style={styles.paragraph}>
              We may update this Privacy Policy periodically. Changes will be
              communicated through in-app notifications, email announcements,
              and website postings. Continued use of our services constitutes
              acceptance of updated terms.
            </p>
          </div>

          <div style={styles.contact}>
            <p style={styles.paragraph}>
              <em>
                This Privacy Policy was last updated on January 1, 2024. For the
                most current version, please visit our website or contact our
                privacy team.
              </em>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
